import React, { useState } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { translate } from '@fiverr-private/i18n-react';
import { Button, IconButton } from '@fiverr-private/button';
import { ShareIcon } from '@fiverr-private/visuals';
import { LazyComponent } from '@fiverr-private/orca';
import { useSocialSharingModal } from '../../hooks/useSocialSharingModal';

const lazyImport = () => import(/* webpackChunkName: 'LazySharingModal' */ '@fiverr-private/sharing_link');

interface ShareButtonProps {
    sellerId: string;
    sellerName: string;
    withLabel?: boolean;
    size?: 'sm' | 'md';
}

export const ShareButton = ({ sellerId, sellerName, withLabel = false, size = 'sm' }: ShareButtonProps) => {
    const [isModalLoaded, setIsModalLoaded] = useState(false);
    const [isOpeningModal, setIsOpeningModal] = useState(false);
    const { locale } = getContext();
    const { openModal } = useSocialSharingModal({
        sellerId,
        sellerName,
    });

    const onClick = () => {
        setIsOpeningModal(true);
        if (isModalLoaded) {
            openModal();
        } else {
            setIsModalLoaded(true);
        }
    };

    const onLoadError = () => {
        setIsModalLoaded(false);
        setIsOpeningModal(false);
    };

    const onLoadSuccess = () => {
        requestAnimationFrame(openModal); // need to send the open modal to next event tick so it will open modal after the load event loop
        setIsOpeningModal(false);
    };

    const ButtonComponent = withLabel ? Button : IconButton;

    const label = translate('seller_page_perseus.professions_page.share');

    return (
        <>
            <ButtonComponent
                size={size}
                intent="secondary"
                variant="outline"
                onClick={onClick}
                disabled={isOpeningModal}
                fullWidth
                aria-label={label}
            >
                <ShareIcon />
                {withLabel && label}
            </ButtonComponent>

            <LazyComponent
                lazyImport={lazyImport}
                shouldImport={isModalLoaded}
                afterLoad={onLoadSuccess}
                onError={onLoadError}
                componentProps={{ locale, isNewSharingFlow: true }}
            />
        </>
    );
};
